import React, { useState } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { Button } from '../../../../../../common/_custom';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { unlockOrRefreshReport, getAllTaskTrackerData } from '../../../../../../store/investor/investorApiHelper';
import { setTaskTrackerMap, setModulePollingList } from '../../../../../../store/investor/action';
import { updateQueryParams } from '../../../../../../utils/searchParamUtils';
import { ctaPurpleRight } from '../../../../../../assets/hostedassets';
import whiteLock from '../../../../../../assets/white-lock.svg';
import styles from './UnlockOverviewSection.module.scss';
import { toast } from 'react-toastify';
import { OverviewData } from '../../../../../../store/dashboard/overview/types';

const UnlockOverviewSection: React.FC<{ overviewData: OverviewData }> = ({ overviewData }) => {
    const { id } = useParams();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const userData = useAppSelector((state) => state.user.userData);
    const selectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const handleUnlockOverview = async () => {
        if (!id && !selectedCompany?.assesseeOrgId) return;

        setIsUnlocking(true);

        const assesseeOrgId = (id || selectedCompany?.assesseeOrgId) as string;
        const unlockPayload = {
            investorOrgId: userData?.investorOrgId,
            assesseeOrgId,
            tabs: ['overview'],
        };

        await unlockOrRefreshReport(unlockPayload, {
            onSuccess: () => {
                // Set overview module to polling list to trigger generating report state
                dispatch(setModulePollingList(['overview']));

                // Fetch task tracker data to setup polling
                getAllTaskTrackerData(
                    { orgId: assesseeOrgId },
                    {
                        onSuccess: (taskTrackerMap) => {
                            dispatch(setTaskTrackerMap(taskTrackerMap));

                            // Use the triggerMain parameter to force Overview component to refetch data without loading state
                            searchParams.set('triggerMain', 'true');
                            searchParams.set('noLoading', 'true');
                            updateQueryParams(searchParams, setSearchParams, {}, location);

                            setIsUnlocking(false);
                        },
                        onError: (error) => {
                            console.error('Failed to fetch task tracker data:', error);
                            toast.error('Failed to unlock overview. Please try again.');
                            setIsUnlocking(false);
                        },
                    },
                );
            },
            onError: () => {
                setIsUnlocking(false);
            },
        });
    };

    const mockFinancialData = {
        headers: [
            { name: 'Metric', key: 'metric' },
            { name: 'FY XXXX', key: 'fy2023' },
            { name: 'FY XXXX', key: 'fy2022' },
            { name: 'FY XXXX', key: 'fy2021' },
        ],
        rows: [
            {
                metric: 'Revenue',
                fy2023: '₹XX,XX,XXX',
                fy2022: '₹XX,XX,XXX',
                fy2021: '₹XX,XX,XXX',
            },
        ],
    };

    return (
        <div className={styles.UnlockOverviewSection}>
            <div className={styles.Container}>
                {/* Header Section */}
                <div className={styles.Header}>
                    <div className={styles.Title}>Financial Summary</div>
                    <div className={styles.Cta}>
                        <img src={ctaPurpleRight} alt="" />
                        View Financial Ratios
                    </div>
                </div>

                {/* Middle Section - Unlock Content */}
                <div className={styles.Content}>
                    <div className={styles.TextContent}>
                        <h3 className={styles.Title}>Unlock Overview to view remaining sections</h3>
                        <p className={styles.Description}>
                            View analytics for Audited Financials, Shareholding Pattern, Charges, Funding Timelines, Management Profile,
                            Employees
                        </p>
                    </div>

                    <div className={styles.ButtonContainer}>
                        <Button
                            text={isUnlocking ? '' : 'Unlock Overview'}
                            onClick={handleUnlockOverview}
                            variant="primary"
                            style={{
                                height: '40px',
                                width: '160px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            icon={isUnlocking ? <LoadingSpinner color="white" height="16px" /> : whiteLock}
                        />
                    </div>
                </div>

                {/* Bottom Section - Blurred Table */}
                <div className={styles.BlurredTable}>
                    <div className={styles.Table}>
                        <div className={styles.TableHeadRow}>
                            {mockFinancialData.headers.map((header, i) => (
                                <div key={i} className={styles.TableHeadRowItem}>
                                    {header.name}
                                </div>
                            ))}
                        </div>

                        {mockFinancialData.rows.map((row, i) => (
                            <div key={i} className={styles.TableBodyRow}>
                                {mockFinancialData.headers.map((header, j) => (
                                    <div key={j} className={styles.TableBodyRowItem}>
                                        {row[header.key]}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnlockOverviewSection;
